import { useState } from "react";
import "@/App.css";
import axios from "axios";
import { toast } from "sonner";
import { MagnifyingGlass, Wrench } from "@phosphor-icons/react";
import { NavTabs } from "@/components/NavTabs";
import { SapConnectionStatus } from "@/components/SapConnectionStatus";
import { ErpConnectionStatus } from "@/components/ErpConnectionStatus";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Oct 2026, user's explicit ask ("reliable tool needed, can we capture
// SAP errors via some SOAP or OData") after several production
// shipments got stuck on the warehouse-movement step this session for
// at least 3 different real causes (stale duplicate SAP delivery,
// genuine RM stock shortfall, SAP ledger-propagation lag). Same shape
// as StoDiagnosticsPage.jsx - search a shipment code, see its full
// movement-attempt history plus a live SAP cross-check per stuck line.
function formatTime(iso) {
  if (!iso) return "-";
  return new Date(iso).toLocaleString();
}

export default function ShipmentDiagnosticsPage() {
  const [docCode, setDocCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [notFound, setNotFound] = useState(false);

  const investigate = async () => {
    if (!docCode.trim()) return;
    setLoading(true);
    setNotFound(false);
    setResult(null);
    try {
      const { data } = await axios.get(`${API}/admin/grn/${docCode.trim()}/investigate`);
      setResult(data);
    } catch (err) {
      if (err?.response?.status === 404) {
        setNotFound(true);
      } else {
        toast.error("Could not load this shipment", { description: err?.response?.data?.detail || err.message });
      }
    } finally {
      setLoading(false);
    }
  };

  const history = result?.movement_attempt_history || [];
  const lines = result?.lines || [];

  return (
    <div className="min-h-screen bg-[#F7F8FA]">
      <div className="bg-[#0E7C86] px-4 sm:px-6 py-3 flex items-center gap-3">
        <Wrench size={22} weight="bold" className="text-white shrink-0" />
        <span className="text-white font-heading font-bold text-base shrink-0">Materials Hub</span>
        <NavTabs />
        <SapConnectionStatus />
        <ErpConnectionStatus />
      </div>

      <div className="max-w-[1100px] mx-auto px-4 sm:px-6 py-6">
        <div className="mb-4">
          <h1 className="text-2xl font-bold text-[#1D2939] font-heading" data-testid="shipment-diagnostics-title">
            Shipment Diagnostics
          </h1>
          <p className="text-sm text-[#667085] font-sans mt-0.5">
            Search a supplier shipment code to see its full warehouse-movement attempt history and a live
            SAP cross-check (delivery status + live inventory at the assumed source area) for any stuck line.
          </p>
        </div>

        <div className="flex gap-2 mb-6">
          <Input
            value={docCode}
            onChange={(e) => setDocCode(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && investigate()}
            placeholder="e.g. S000257"
            className="max-w-[280px]"
            data-testid="shipment-diagnostics-search-input"
          />
          <Button onClick={investigate} disabled={loading || !docCode.trim()} data-testid="shipment-diagnostics-search-button" className="gap-1.5">
            <MagnifyingGlass size={14} weight="bold" />
            {loading ? "Searching..." : "Investigate"}
          </Button>
        </div>

        {notFound && (
          <div className="bg-white border border-[#D0D5DD] rounded-md px-4 py-6 text-center text-sm text-[#667085]" data-testid="shipment-diagnostics-not-found">
            No shipment found with code "{docCode.trim()}".
          </div>
        )}

        {result && (
          <div className="space-y-6" data-testid="shipment-diagnostics-result">
            <div className="bg-white border border-[#D0D5DD] rounded-md p-4">
              <h2 className="text-sm font-semibold text-[#1D2939] mb-2">Shipment Summary</h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
                <div><div className="text-xs text-[#98A2B3]">Status</div><div className="font-medium">{result.status}</div></div>
                <div><div className="text-xs text-[#98A2B3]">Movement Status</div><div className="font-medium">{result.sap_movement_status || "-"}</div></div>
              </div>
            </div>

            <div className="bg-white border border-[#D0D5DD] rounded-md p-4">
              <h2 className="text-sm font-semibold text-[#1D2939] mb-3">Line Diagnosis ({lines.length})</h2>
              <div className="space-y-3">
                {lines.map((l, i) => (
                  <div key={i} className="border border-[#EAECF0] rounded p-3" data-testid={`shipment-diagnostics-line-${i}`}>
                    <div className="text-sm font-medium mb-1">PO {l.po_number} - {l.product_id} (shipped {l.shipped_qty})</div>
                    <div className="text-xs text-[#667085] mb-1">Assumed source area: {l.assumed_source_area || "-"}</div>
                    {l.movement_ok === false && <div className="text-xs text-[#B42318] mb-1">Movement error: {l.movement_error}</div>}
                    {l.diagnosis && <div className="text-sm font-medium text-[#0E7C86] mt-2">{l.diagnosis}</div>}
                    {l.sap_delivery_rows && (
                      <pre className="text-xs bg-[#F7F8FA] rounded p-2 mt-2 overflow-x-auto whitespace-pre-wrap">{JSON.stringify(l.sap_delivery_rows, null, 2)}</pre>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white border border-[#D0D5DD] rounded-md p-4">
              <h2 className="text-sm font-semibold text-[#1D2939] mb-3">Movement Attempt History ({history.length})</h2>
              {history.length === 0 ? (
                <div className="text-sm text-[#98A2B3]" data-testid="shipment-diagnostics-history-empty">No movement attempts recorded yet.</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b border-[#EAECF0] text-left text-xs text-[#667085]">
                        <th className="px-2 py-1.5 font-medium">Time</th>
                        <th className="px-2 py-1.5 font-medium">Line</th>
                        <th className="px-2 py-1.5 font-medium">OK</th>
                        <th className="px-2 py-1.5 font-medium">Error</th>
                      </tr>
                    </thead>
                    <tbody>
                      {history.slice().reverse().map((h, i) => (
                        <tr key={i} className="border-b border-[#F2F4F7]" data-testid={`shipment-diagnostics-history-row-${i}`}>
                          <td className="px-2 py-1.5 text-[#475467] whitespace-nowrap">{formatTime(h.attempted_at)}</td>
                          <td className="px-2 py-1.5">{h.po_number} / {h.product_id}</td>
                          <td className="px-2 py-1.5">{h.ok ? "Yes" : "No"}</td>
                          <td className="px-2 py-1.5 text-[#475467]">{h.error || "-"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
